import React, { useState, useEffect } from 'react';
import { MagnifyingGlass, X, Buildings, User, IdentificationCard, Calendar, Phone, Envelope } from '@phosphor-icons/react';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { useTranslationWithLanguage } from '../context/language-context';
import { searchEstablishments, Establishment } from '../lib/mock-establishments';
import { searchEmployees, searchWpsIssues, searchAbscondingEmployees, Employee } from '../lib/mock-employees';

interface GlobalSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

export function GlobalSearch({ isOpen, onClose }: GlobalSearchProps) {
  const { t, language } = useTranslationWithLanguage();
  const [searchType, setSearchType] = useState<'establishment' | 'wps' | 'absconding'>('establishment');
  const [searchQuery, setSearchQuery] = useState('');
  const [isEmiratesId, setIsEmiratesId] = useState(false);
  const [searchResults, setSearchResults] = useState<{
    establishments: Establishment[];
    employees: Employee[];
  }>({
    establishments: [],
    employees: []
  });
  const [isSearching, setIsSearching] = useState(false);

  // Reset search results when search type changes
  useEffect(() => {
    setSearchResults({ establishments: [], employees: [] });
    setSearchQuery('');
    setIsEmiratesId(false);
  }, [searchType]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSearching(true);

    if (searchType === 'establishment') {
      if (isEmiratesId && validateEmiratesId(searchQuery)) {
        // Search for employee by Emirates ID
        const employees = searchEmployees(searchQuery);
        setSearchResults({ establishments: [], employees });
      } else if (searchQuery.trim()) {
        // Search for establishments and employees by name
        const establishments = searchEstablishments(searchQuery);
        const employees = searchEmployees(searchQuery);
        setSearchResults({ establishments, employees });
      } else {
        setSearchResults({ establishments: [], employees: [] });
      }
    } else if (searchType === 'wps') {
      // For WPS search, we'll show all employees with WPS issues
      const employees = searchWpsIssues();
      setSearchResults({ establishments: [], employees });
    } else if (searchType === 'absconding') {
      // For absconding search, we'll show all absconding employees
      const employees = searchAbscondingEmployees();
      setSearchResults({ establishments: [], employees });
    }

    setIsSearching(false);
  };

  const validateEmiratesId = (value: string) => {
    // Simple Emirates ID validation - 15 digits
    return /^\d{15}$/.test(value);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-16 px-4 md:pt-24">
      <div
        className="w-full max-w-md bg-white rounded-lg shadow-lg overflow-hidden max-h-[80vh] flex flex-col"
        dir={language === 'ar' ? 'rtl' : 'ltr'}
      >
        <div className="bg-[var(--color-gold-500)] p-4 flex items-center justify-between">
          <h2 className="text-white text-lg font-medium">{t('search.globalSearch')}</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center bg-white/20 text-white"
          >
            <X weight="bold" className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          <div className="flex space-x-2 mb-4 rtl:space-x-reverse">
            <button
              className={`flex-1 py-2 px-3 rounded-md text-sm font-medium ${
                searchType === 'establishment'
                  ? 'bg-[var(--color-gold-500)] text-white'
                  : 'bg-gray-100 text-gray-700'
              }`}
              onClick={() => setSearchType('establishment')}
            >
              {t('search.establishment')}
            </button>
            <button
              className={`flex-1 py-2 px-3 rounded-md text-sm font-medium ${
                searchType === 'wps'
                  ? 'bg-[var(--color-gold-500)] text-white'
                  : 'bg-gray-100 text-gray-700'
              }`}
              onClick={() => setSearchType('wps')}
            >
              {t('search.wps')}
            </button>
            <button
              className={`flex-1 py-2 px-3 rounded-md text-sm font-medium ${
                searchType === 'absconding'
                  ? 'bg-[var(--color-gold-500)] text-white'
                  : 'bg-gray-100 text-gray-700'
              }`}
              onClick={() => setSearchType('absconding')}
            >
              {t('search.absconding')}
            </button>
          </div>

          <form onSubmit={handleSearch}>
            <div className="relative mb-4">
              <Input
                type="text"
                placeholder={
                  searchType === 'establishment'
                    ? isEmiratesId
                      ? t('search.enterEmiratesId')
                      : t('search.enterEstablishmentOrEmployee')
                    : searchType === 'wps'
                    ? t('search.enterWpsInfo')
                    : t('search.enterAbscondingInfo')
                }
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  if (searchType === 'establishment') {
                    // Check if input looks like an Emirates ID
                    setIsEmiratesId(/^\d+$/.test(e.target.value));
                  }
                }}
                className="pr-10"
              />
              <button
                type="submit"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 w-8 h-8 flex items-center justify-center text-gray-500"
              >
                <MagnifyingGlass weight="bold" className="w-5 h-5" />
              </button>
            </div>

            {searchType === 'establishment' && isEmiratesId && (
              <div className="mb-4">
                <div className={`text-sm ${validateEmiratesId(searchQuery) ? 'text-green-600' : 'text-amber-600'}`}>
                  {validateEmiratesId(searchQuery)
                    ? t('search.validEmiratesId')
                    : t('search.invalidEmiratesId')}
                </div>
              </div>
            )}

            <div className="text-sm text-gray-500 mb-4">
              {searchType === 'establishment' && (
                <p>{t('search.establishmentDescription')}</p>
              )}
              {searchType === 'wps' && (
                <p>{t('search.wpsDescription')}</p>
              )}
              {searchType === 'absconding' && (
                <p>{t('search.abscondingDescription')}</p>
              )}
            </div>
          </form>
        </div>

        {/* Search Results */}
        <div className="overflow-y-auto flex-1 px-4 pb-4">
          {isSearching ? (
            <div className="text-center py-4">
              <p>{t('common.searching')}...</p>
            </div>
          ) : (
            <>
              {/* Establishment Results */}
              {searchResults.establishments.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-md font-medium mb-2 text-gray-700">{t('establishment.establishmentState')}</h3>
                  <div className="space-y-3">
                    {searchResults.establishments.map((establishment) => (
                      <Card key={establishment.id} className="overflow-hidden">
                        <CardContent className="p-3">
                          <div className="flex items-start gap-3">
                            <div className="p-2 bg-opacity-10 bg-[var(--color-gold-500)] rounded-lg">
                              <Buildings className="w-5 h-5 text-[var(--color-gold-500)]" />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-medium">{establishment.name}</h4>
                              <div className="text-sm text-gray-500 mt-1">
                                <div className="flex items-center gap-1">
                                  <IdentificationCard className="w-4 h-4" />
                                  <span>{establishment.licenseNumber}</span>
                                </div>
                                <div className="flex items-center gap-1 mt-1">
                                  <Calendar className="w-4 h-4" />
                                  <span>{establishment.expiryDate}</span>
                                </div>
                                <div className="flex items-center gap-1 mt-1">
                                  <Phone className="w-4 h-4" />
                                  <span>{establishment.phone}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 mt-2">
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  establishment.status === 'active'
                                    ? 'bg-green-100 text-green-800'
                                    : establishment.status === 'inactive'
                                    ? 'bg-gray-100 text-gray-800'
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {establishment.status}
                                </span>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  establishment.riskLevel === 'low'
                                    ? 'bg-green-100 text-green-800'
                                    : establishment.riskLevel === 'medium'
                                    ? 'bg-amber-100 text-amber-800'
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {establishment.riskLevel} risk
                                </span>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Employee Results */}
              {searchResults.employees.length > 0 && (
                <div>
                  <h3 className="text-md font-medium mb-2 text-gray-700">{t('establishment.workersDetails')}</h3>
                  <div className="space-y-3">
                    {searchResults.employees.map((employee) => (
                      <Card key={employee.id} className="overflow-hidden">
                        <CardContent className="p-3">
                          <div className="flex items-start gap-3">
                            <div className="p-2 bg-opacity-10 bg-[var(--color-gold-500)] rounded-lg">
                              <User className="w-5 h-5 text-[var(--color-gold-500)]" />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-medium">{employee.name}</h4>
                              <div className="text-sm text-gray-500 mt-1">
                                <div className="flex items-center gap-1">
                                  <IdentificationCard className="w-4 h-4" />
                                  <span>{employee.emiratesId}</span>
                                </div>
                                <div className="flex items-center gap-1 mt-1">
                                  <Buildings className="w-4 h-4" />
                                  <span>{employee.establishmentName}</span>
                                </div>
                                <div className="flex items-center gap-1 mt-1">
                                  <Calendar className="w-4 h-4" />
                                  <span>Joined: {employee.joiningDate}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 mt-2">
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  employee.status === 'active'
                                    ? 'bg-green-100 text-green-800'
                                    : employee.status === 'inactive'
                                    ? 'bg-gray-100 text-gray-800'
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {employee.status}
                                </span>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  employee.wpsStatus === 'paid'
                                    ? 'bg-green-100 text-green-800'
                                    : employee.wpsStatus === 'delayed'
                                    ? 'bg-amber-100 text-amber-800'
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  WPS: {employee.wpsStatus}
                                </span>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* No Results */}
              {searchQuery &&
               searchType === 'establishment' &&
               searchResults.establishments.length === 0 &&
               searchResults.employees.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-gray-500">{t('plan.noEstablishmentsFound')}</p>
                  <p className="text-sm text-gray-400 mt-1">{t('plan.tryAdjustingSearch')}</p>
                </div>
              )}

              {/* Initial state for WPS and Absconding */}
              {searchType === 'wps' && !searchResults.employees.length && (
                <div className="text-center py-4">
                  <p className="text-gray-500">{t('search.wpsDescription')}</p>
                  <button
                    onClick={handleSearch}
                    className="mt-2 px-4 py-2 bg-[var(--color-gold-500)] text-white rounded-md text-sm"
                  >
                    {t('common.search')}
                  </button>
                </div>
              )}

              {searchType === 'absconding' && !searchResults.employees.length && (
                <div className="text-center py-4">
                  <p className="text-gray-500">{t('search.abscondingDescription')}</p>
                  <button
                    onClick={handleSearch}
                    className="mt-2 px-4 py-2 bg-[var(--color-gold-500)] text-white rounded-md text-sm"
                  >
                    {t('common.search')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}